import React, { useMemo, useState } from 'react';
import Accordion from '@ui/Accordion';

type FAQProp = {
  answer: string;
  question: string;
};

type FAQContentProps = {
  content?: FAQProp[];
  /**
   * Callers that already emit their own FAQPage JSON-LD for this same
   * content (e.g. the /c/ category page's <FAQSchema>) should pass false
   * here to avoid publishing two competing FAQPage entities on one page.
   * Defaults to true so every existing caller keeps today's behavior.
   */
  emitFaqPageMicrodata?: boolean;
};

const FAQContent = ({ content, emitFaqPageMicrodata = true }: FAQContentProps) => {
  const [currentQuestion, setCurrentQuestion] = useState<string | undefined>('');

  // `content` traces back to Firestore-sourced data (category.faq,
  // master.gmFaq, etc.) with no runtime validation at the read boundary —
  // the TypeScript type is a compile-time promise, not a guarantee. A
  // malformed value (a non-array, or an entry missing question/answer)
  // must be filtered here rather than trusted, or .map/property access
  // below throws during render and 500s every page that renders this
  // shared component.
  const validEntries = useMemo(
    () =>
      Array.isArray(content)
        ? content.filter(
            (item): item is FAQProp =>
              !!item &&
              typeof item.question === 'string' &&
              item.question.length > 0 &&
              typeof item.answer === 'string' &&
              item.answer.length > 0
          )
        : [],
    [content]
  );

  const faqPageSchemaProps = emitFaqPageMicrodata
    ? { itemScope: true, itemType: 'https://schema.org/FAQPage' }
    : {};
  const questionSchemaProps = emitFaqPageMicrodata
    ? { itemScope: true, itemProp: 'mainEntity', itemType: 'https://schema.org/Question' }
    : {};
  const answerSchemaProps = emitFaqPageMicrodata
    ? { itemScope: true, itemProp: 'acceptedAnswer', itemType: 'https://schema.org/Answer' }
    : {};
  const nameItemProp = emitFaqPageMicrodata ? 'name' : undefined;
  const textItemProp = emitFaqPageMicrodata ? 'text' : undefined;

  return (
    <>
      {validEntries.length > 0 && (
        <div className={`flex w-full flex-col items-center gap-[2.4rem] px-0 sm:px-[5rem] py-0 sm:py-[4rem]`} {...faqPageSchemaProps}>
          <h1 className="text-[2.4rem] font-semibold text-brand-lightest-black sm:text-[2.8rem]">
            Frequently Asked Questions
          </h1>
          <div className="gap-8 mt-[3rem] mb-[6rem] w-full max-w-[100rem]">
            {validEntries.map((item, index) => (
              <div key={`faq-item-${index}`} {...questionSchemaProps}>
                <Accordion
                  title={item.question}
                  divider={true}
                  hover="text"
                  intent="faq"
                  highlightHeaderOnOpen={true}
                  startOpen={currentQuestion === item.question}
                  onOpen={() => setCurrentQuestion(item.question)}
                  onClose={() => setCurrentQuestion(undefined)}
                  itemProp={nameItemProp}
                >
                  <div className="pl-[1.6rem] pb-[4rem]" {...answerSchemaProps}>
                    <p itemProp={textItemProp}>{item.answer}</p>
                  </div>
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default FAQContent;
